import React from 'react';
import styled from 'styled-components';
import { Icon } from '@tviklax/ui-components';
import { PerfMetric } from './types';

const Card = styled.div`
  background: white;
  border-radius: 16px;
  padding: 24px;
  box-shadow: ${props => props.theme.shadows.md};
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 24px;
`;

const Title = styled.h3`
  font-size: 1rem;
  font-weight: 700;
  color: ${props => props.theme.colors.gray800};
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;

  @media (min-width: 480px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const MetricBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Label = styled.span`
  font-size: 0.8125rem;
  color: ${props => props.theme.colors.gray500};
  font-weight: 500;
`;

const Value = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  color: ${props => props.theme.colors.gray800};
`;

const Trend = styled.div<{ $trend: 'up' | 'down' }>`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 0.75rem;
  font-weight: 600;
  color: ${props => props.$trend === 'up' ? props.theme.colors.success : props.theme.colors.error};

  svg {
    width: 12px;
    height: 12px;
  }
`;

const SectionLabel = styled.div`
  font-size: 0.65rem;
  font-weight: 800;
  color: ${props => props.theme.colors.gray400};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 12px;
  grid-column: span 1;
`;

interface SLAMetricsProps {
  metrics: PerfMetric[];
}

export const SLAMetrics: React.FC<SLAMetricsProps> = ({ metrics }) => {
  const speedMetrics = metrics.filter(m => m.label.toLowerCase().includes('time') || m.label.toLowerCase().includes('clearance'));
  const reliabilityMetrics = metrics.filter(m => m.label.toLowerCase().includes('delivery') || m.label.toLowerCase().includes('compliance'));

  return (
    <Card>
      <Header>
        <Icon name="TrendingUp" color="#f86400" size={20} />
        <Title>SLA & Performance</Title>
      </Header>
      <Grid>
        <div>
          <SectionLabel>Speed</SectionLabel>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            {speedMetrics.map((metric) => (
              <MetricBox key={metric.label}>
                <Label>{metric.label}</Label>
                <Value>{metric.value}</Value>
                <Trend $trend={metric.trend}>
                  <Icon name={metric.trend === 'up' ? 'TrendingUp' : 'TrendingDown'} />
                  {metric.trendValue}
                </Trend>
              </MetricBox>
            ))}
          </div>
        </div>
        <div>
          <SectionLabel>Reliability</SectionLabel>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            {reliabilityMetrics.map((metric) => (
              <MetricBox key={metric.label}>
                <Label>{metric.label}</Label>
                <Value>{metric.value}</Value>
                <Trend $trend={metric.trend}>
                  <Icon name={metric.trend === 'up' ? 'TrendingUp' : 'TrendingDown'} />
                  {metric.trendValue}
                </Trend>
              </MetricBox>
            ))}
          </div>
        </div>
      </Grid>
    </Card>
  );
};
